import { isTokenValid, newIsTokenValid } from "../services/jwt";

// Helper function to conditionally join class names
export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Format price with commas and currency symbol
export function formatPrice(price) {
  const numPrice = typeof price === "string" ? Number.parseFloat(price) : price;

  if (isNaN(numPrice)) return "$0";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numPrice);
}
export function base64ToFile(base64String, filename, mimeType) {
  const byteString = atob(base64String.split(",")[1]); // decode base64
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ab], filename, { type: mimeType });
}

export function isAuthenticated() {
  const token = localStorage.getItem("access_token");

  const refreshTokenString = localStorage.getItem("token");
  if (!token && !refreshTokenString) {
    return false;
  }

  const refreshToken = JSON.parse(refreshTokenString).refresh;
  if (!newIsTokenValid(token, refreshToken)) {
    localStorage.clear();
    return false;
  }
  return true;
}

export const formatDate = dateString => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

// Function to convert an array of files to a base64 string
export const filesToBase64 = async files => {
  // Create an array to hold all the promises
  const promises = files.map(file => {
    return new Promise(resolve => {
      const reader = new FileReader();

      reader.onload = () => {
        // Get the base64 string
        const base64String = reader.result;

        // Create an object with file metadata and base64 data
        const fileData = {
          name: file.name,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified,
          data: base64String
        };

        // Resolve with the stringified object
        resolve(JSON.stringify(fileData));
      };

      // Read the file as a data URL (base64)
      reader.readAsDataURL(file);
    });
  });

  // Wait for all files to be processed
  const base64Array = await Promise.all(promises);

  // Join all the stringified objects with a special separator
  // Using a custom separator that's unlikely to appear in the base64 data
  return base64Array.join("|||FILE_SEPARATOR|||");
};

// Function to convert a base64 string back to an array of files
export const base64ToFiles = base64String => {
  // If the string is empty, return an empty array
  if (!base64String) return [];

  // Split the string by the separator
  const fileDataArray = base64String.split("|||FILE_SEPARATOR|||");

  // Convert each part back to a File object
  return fileDataArray.map(fileDataString => {
    // Parse the JSON string back to an object
    const fileData = JSON.parse(fileDataString);

    // Extract the base64 data (remove the data URL prefix if present)
    const base64Data = fileData.data.split(",")[1] || fileData.data;

    // Convert base64 to binary
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create Blob from binary data
    const blob = new Blob([bytes], { type: fileData.type });

    // Create File object from Blob
    return new File([blob], fileData.name, {
      type: fileData.type,
      lastModified: fileData.lastModified
    });
  });
};

export function slugify(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "") // Remove invalid chars
    .replace(/\s+/g, "-") // Replace whitespace with dashes
    .replace(/-+/g, "-"); // Collapse multiple dashes
}

export const generateMapDetails = (
  venueName = "",
  street = "",
  city = "",
  state = "",
  zip = ""
) => {
  const address = `${venueName},${street},${city},${state},${zip}`;
  const encodedAddress = encodeURIComponent(address);
  const apiKey = process.env.REACT_APP_GOOGLEMAP_API || "";

  return {
    navigation_links: {
      driving: `https://maps.google.com?saddr=Current+Location&daddr=${encodedAddress}&mode=driving`,
      transit: `https://maps.google.com?saddr=Current+Location&daddr=${encodedAddress}&mode=transit&dirflg=r`,
      biking: `https://maps.google.com?saddr=Current+Location&daddr=${encodedAddress}&mode=bicycling&dirflg=b`,
      walking: `https://maps.google.com?saddr=Current+Location&daddr=${encodedAddress}&dirflg=w`
    },
    map_url: `https://maps.googleapis.com/maps/api/staticmap?center=${encodedAddress}&markers=color:red%7Clabel:C%7C${encodedAddress}&zoom=13&size=600x300&maptype=roadmap&key=${apiKey}`,
    map_title: `${venueName},${street}, ${city}, ${state}, ${zip}`
  };
};

export const transformEventData = raw => {
  const safeParseJSON = (input, fallback) => {
    try {
      return JSON.parse(input.replace(/'/g, '"'));
    } catch {
      return fallback;
    }
  };
  const safeParseTicketsJSON = (input, fallback) => {
    try {
      const normalized = input
        .replace(/'/g, '"')
        .replace(/\bTrue\b/g, "true")
        .replace(/\bFalse\b/g, "false");

      return JSON.parse(normalized);
    } catch {
      return fallback;
    }
  };

  const mapDetails = generateMapDetails(
    raw.venue_name ?? raw.venue ?? "",
    raw.venue_full_address ?? raw.location ?? "",
    raw.venue_street ?? "",
    raw.venue_city ?? raw.city ?? "",
    raw.venue_state ?? raw.state ?? "",
    raw.venue_zip ?? ""
  );

  return {
    id: raw.event_id ?? "",
    title: raw.name ?? "",
    link: raw.event_url ?? "",
    date: raw.event_date?.split(",")[0]?.trim() ?? raw.date,
    time:
      raw.event_date?.split(",")[2]?.trim() ??
      raw.time ??
      `${raw.eventstarttime} - ${raw.eventendtime}`,
    venue: raw.venue ?? "",
    location: `${raw.city ?? ""}, ${raw.state ?? ""}`,
    price: raw.price ?? "",
    status: raw.status ?? "",
    category: raw.category ?? null,
    performers: safeParseJSON(raw.performers ?? "[]", []),
    image: raw.cover_image ?? "",
    action_type: raw.action_type ?? "",
    event_url: raw.event_url?.split("https://events.sulekha.com/")[1] ?? "",
    description: raw.description ?? "",
    venue_details: {
      name: raw.venue_name ?? raw.venue ?? "",
      full_address: raw.venue_full_address ?? raw.location ?? "",
      street_address: raw.venue_street ?? "",
      city: raw.venue_city ?? raw.city ?? "",
      state: raw.venue_state ?? raw.state ?? "",
      zip_code: raw.venue_zip ?? "",

      ...mapDetails
    },
    artist_details: {
      image: raw.artist_image ?? "",
      image_alt: `${raw.artist_name ?? ""} Tickets and Concert Dates`,
      name: raw.artist_name ?? "",
      link: raw.artist_link ?? "",
      link_title: `${raw.artist_name ?? ""} Tickets and Concert Dates`,
      description: raw.artist_description ?? ""
    },
    organizer_details: {
      title: "Organizer Details",
      logo: raw.organizer_logo ?? "",
      logo_title: raw.organizer_name ?? "",
      name: raw.organizer_name ?? "",
      events_link: raw.organizer_events_link ?? "",
      events_link_title: raw.organizer_name ?? "",
      upcoming_events_count: raw.organizer_upcoming_count ?? "0",
      follow_link_available: !!raw.organizer_follow_available
    },
    ticket_information: {
      title: "Ticket Information",
      ticket_types: safeParseTicketsJSON(raw.ticket_types ?? "[]", []),
      action_button: {
        text: raw.ticket_action_button ?? "",
        onclick: "onIWantMySeats('show');"
      }
    }
  };
};

//for getting userid
export function getUserId() {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return null;
    }

    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }

    const payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");

    // Decode base64 payload
    const decodedPayload = JSON.parse(
      decodeURIComponent(
        atob(payloadBase64)
          .split("")
          .map(c => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join("")
      )
    );

    return decodedPayload.userId || decodedPayload.userid || null;
  } catch (error) {
    console.error("Error extracting user ID from token:", error);
    return null;
  }
}
